import { useEffect, useMemo, useState } from "react";
import { api, extractError } from "@/lib/api";
import { toast } from "sonner";
import { ChevronLeft, ChevronRight, CalendarDays } from "lucide-react";

const WD_SHORT = ["Seg", "Ter", "Qua", "Qui", "Sex", "Sáb", "Dom"];

function startOfWeek(d) {
  const x = new Date(d);
  const day = (x.getDay() + 6) % 7; // 0=Mon
  x.setDate(x.getDate() - day);
  x.setHours(0, 0, 0, 0);
  return x;
}
function fmt(d) {
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, "0");
  const dd = String(d.getDate()).padStart(2, "0");
  return `${y}-${m}-${dd}`;
}
function addDays(d, n) { const x = new Date(d); x.setDate(x.getDate() + n); return x; }

export default function Schedule() {
  const [cities, setCities] = useState([]);
  const [cityId, setCityId] = useState("");
  const [anchor, setAnchor] = useState(() => startOfWeek(new Date()));
  const [data, setData] = useState({}); // date -> availability payload
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    api.get("/cities").then((r) => {
      const active = r.data.filter((c) => c.is_active);
      setCities(active);
      if (active.length > 0 && !cityId) setCityId(active[0].id);
    });
  }, [cityId]);

  const days = useMemo(() => Array.from({ length: 7 }, (_, i) => addDays(anchor, i)), [anchor]);

  useEffect(() => {
    if (!cityId) return;
    setLoading(true);
    const promises = days.map((d) => api.get("/schedule/availability", { params: { city_id: cityId, date: fmt(d) } }).then((r) => [fmt(d), r.data]));
    Promise.all(promises)
      .then((entries) => setData(Object.fromEntries(entries)))
      .catch((e) => toast.error(extractError(e)))
      .finally(() => setLoading(false));
  }, [cityId, anchor, days]);

  const currentCity = cities.find((c) => c.id === cityId);
  const allSlots = useMemo(() => currentCity?.default_slots || [], [currentCity]);

  const weekLabel = `${anchor.toLocaleDateString("pt-BR", { day: "2-digit", month: "short" })} — ${addDays(anchor, 6).toLocaleDateString("pt-BR", { day: "2-digit", month: "short", year: "numeric" })}`;
  const today = fmt(new Date());

  return (
    <div className="space-y-6" data-testid="schedule-page">
      <div className="flex items-end justify-between gap-4 flex-wrap">
        <div>
          <div className="inline-block text-[10px] uppercase tracking-[0.3em] font-bold px-2 py-0.5 bg-[#FFD400] text-[#0B1F3A] rounded-sm">Fase 2 · Agenda</div>
          <h1 className="font-display text-3xl sm:text-4xl font-black tracking-tighter mt-2 text-[#0B1F3A]">Agenda</h1>
          <p className="text-sm text-[#6B7280] mt-1">Visão semanal de disponibilidade e reservas por cidade.</p>
        </div>
        <div className="flex items-center gap-2">
          <select value={cityId} onChange={(e) => setCityId(e.target.value)}
            className="bg-white border border-[#E5E7EB] text-sm rounded px-3 py-2 focus:border-[#0B1F3A] focus:outline-none"
            data-testid="schedule-city-select">
            {cities.map((c) => <option key={c.id} value={c.id}>{c.name}/{c.state}</option>)}
          </select>
          <button onClick={() => setAnchor(addDays(anchor, -7))} className="p-2 bg-white border border-[#E5E7EB] rounded hover:border-[#0B1F3A]" data-testid="schedule-prev-week"><ChevronLeft size={16} /></button>
          <button onClick={() => setAnchor(startOfWeek(new Date()))} className="px-3 py-2 text-xs bg-white border border-[#E5E7EB] rounded hover:border-[#0B1F3A] font-semibold" data-testid="schedule-today-btn">Hoje</button>
          <button onClick={() => setAnchor(addDays(anchor, 7))} className="p-2 bg-white border border-[#E5E7EB] rounded hover:border-[#0B1F3A]" data-testid="schedule-next-week"><ChevronRight size={16} /></button>
        </div>
      </div>

      <div className="flex items-center gap-2 text-sm">
        <CalendarDays size={14} className="text-[#0B1F3A]" />
        <span className="font-display font-bold text-[#0B1F3A]">{weekLabel}</span>
        {loading && <span className="text-xs text-[#6B7280]">carregando…</span>}
      </div>

      <div className="border border-[#E5E7EB] bg-white rounded-md overflow-x-auto">
        <table className="w-full text-sm">
          <thead>
            <tr className="text-[10px] uppercase tracking-[0.2em] text-[#6B7280] border-b border-[#E5E7EB] bg-[#F9FAFB]">
              <th className="px-3 py-2 text-left font-bold w-24">Horário</th>
              {days.map((d, i) => {
                const iso = fmt(d);
                const isToday = iso === today;
                return (
                  <th key={iso} className={`px-2 py-2 text-center font-bold ${isToday ? "text-[#0B1F3A]" : ""}`}>
                    <div>{WD_SHORT[i]}</div>
                    <div className={`text-sm font-mono-data mt-0.5 ${isToday ? "text-[#FFD400] bg-[#0B1F3A] rounded-sm px-1 inline-block" : "text-[#0A0A0A]"}`}>{d.toLocaleDateString("pt-BR", { day: "2-digit", month: "2-digit" })}</div>
                  </th>
                );
              })}
            </tr>
          </thead>
          <tbody>
            {allSlots.length === 0 && (
              <tr><td colSpan={8} className="px-4 py-8 text-center text-[#9CA3AF]">Selecione uma cidade.</td></tr>
            )}
            {allSlots.map((time) => (
              <tr key={time} className="border-b border-[#F1F2F4]">
                <td className="px-3 py-2 font-mono-data text-xs text-[#0B1F3A] font-semibold">{time}</td>
                {days.map((d) => {
                  const iso = fmt(d);
                  const day = data[iso];
                  const slot = day?.slots?.find((s) => s.time === time);
                  if (!day) return <td key={iso} className="px-1 py-1 text-center text-xs text-[#9CA3AF]">…</td>;
                  if (day.closed) return <td key={iso} className="px-1 py-1 text-center bg-[#F5F6F8] text-[10px] text-[#9CA3AF]">—</td>;
                  const testKey = `slot-${iso}-${time.replace(":","")}`;
                  if (slot?.available) return <td key={iso} className="px-1 py-1 text-center" data-testid={testKey}><span className="inline-block px-2 py-0.5 text-[10px] uppercase tracking-widest font-bold bg-emerald-50 text-emerald-700 rounded-sm">Livre</span></td>;
                  return <td key={iso} className="px-1 py-1 text-center" data-testid={testKey}><span className="inline-block px-2 py-0.5 text-[10px] uppercase tracking-widest font-bold bg-[#0B1F3A] text-[#FFD400] rounded-sm" title={slot?.info?.inspection_type}>Ocupado</span></td>;
                })}
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      <div className="text-xs text-[#6B7280]">
        Regras da cidade: até {currentCity?.large_property_threshold || 130} m² = 1 horário · acima disso = 2 horários consecutivos · prazo mínimo {currentCity?.min_lead_hours || 12}h · antecedência máxima {currentCity?.max_advance_days || 60} dias.
      </div>
    </div>
  );
}
